import { useState, useEffect } from 'react';
import { X, DownloadSimple, Warning, FilePdf, Eye } from '@phosphor-icons/react';
import LoadingSpinner from './LoadingSpinner';

/**
 * AttachmentViewer - Modal for viewing PDFs and images
 * Supports inline viewing with mobile-friendly fallback for PDFs
 */
export default function AttachmentViewer({ 
  isOpen, 
  onClose, 
  attachment,
  blobUrl,
  isLoading = false,
  error = null,
}) {
  const [loadError, setLoadError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      // Check for mobile user agent or small screen
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileUA || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Reset error state when attachment changes
  useEffect(() => {
    setLoadError(false);
  }, [attachment?.id]);
  
  // Close on escape key
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  const isPdf = attachment?.file_type === 'application/pdf';
  const isImage = attachment?.file_type?.startsWith('image/');
  
  // Format file size
  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  
  // Handle download
  function handleDownload() {
    if (!blobUrl) return;
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  // Handle opening PDF in system viewer (for mobile)
  function handleOpenInViewer() {
    if (!blobUrl) return;
    
    // Create a download that triggers system PDF viewer
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = attachment.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  return (
    <div className="fixed inset-0 z-[70] overflow-hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative h-full flex flex-col">
        {/* Header */}
        <div 
          className="flex-shrink-0 flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-white font-medium truncate">
              {attachment?.filename || 'Attachment'}
            </h3>
            {attachment?.file_size && (
              <span className="text-gray-400 text-sm flex-shrink-0">
                {formatFileSize(attachment.file_size)}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {blobUrl && (
              <button
                onClick={handleDownload}
                className="p-2 rounded-sm transition-colors text-gray-400 hover:text-white hover:bg-white/10"
                title="Download"
              >
                <DownloadSimple size={20} weight="bold" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-sm transition-colors text-gray-400 hover:text-white hover:bg-white/10"
              title="Close"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner size="lg" />
              <span className="text-gray-400">Loading...</span>
            </div>
          ) : error || loadError ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <Warning size={48} className="text-amber-500" />
              <p className="text-white font-medium">Unable to preview file</p>
              <p className="text-gray-400 text-sm max-w-sm">
                {error || 'The file could not be loaded. It may not be cached for offline viewing.'}
              </p>
              {blobUrl && (
                <button
                  onClick={handleDownload}
                  className="mt-2 px-4 py-2 bg-white/10 text-white rounded-sm hover:bg-white/20 transition-colors"
                >
                  Download Instead
                </button>
              )}
            </div>
          ) : !blobUrl ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <Warning size={48} className="text-gray-500" />
              <p className="text-gray-400">No file to display</p>
            </div>
          ) : isPdf && isMobile ? (
            // Mobile PDF - Show friendly download/open UI since iframes don't work
            <div className="flex flex-col items-center gap-4 text-center p-6 rounded-lg bg-white/5 max-w-sm">
              <div className="w-20 h-20 rounded-lg bg-red-500/20 flex items-center justify-center">
                <FilePdf size={48} className="text-red-400" weight="fill" />
              </div>
              <div>
                <p className="text-white font-medium mb-1">{attachment.filename}</p>
                <p className="text-gray-400 text-sm">{formatFileSize(attachment.file_size)}</p>
              </div>
              <p className="text-gray-400 text-sm">
                PDF preview is not available on mobile browsers. Tap below to open in your device's PDF viewer.
              </p>
              <button
                onClick={handleOpenInViewer}
                className="w-full px-6 py-3 bg-white text-gray-900 rounded-sm font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
              >
                <Eye size={20} weight="bold" />
                Open PDF
              </button>
              <button
                onClick={handleDownload}
                className="text-gray-400 text-sm hover:text-white transition-colors"
              >
                or download to device
              </button>
            </div>
          ) : isPdf ? (
            // Desktop PDF Viewer - use object tag with iframe fallback
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full h-full max-w-4xl rounded-sm"
              style={{ 
                backgroundColor: 'white',
                minHeight: '80vh',
              }}
            >
              {/* Fallback if object doesn't work */}
              <iframe
                src={blobUrl}
                className="w-full h-full"
                style={{ 
                  backgroundColor: 'white',
                  minHeight: '80vh',
                }}
                title={attachment?.filename}
                onError={() => setLoadError(true)}
              />
            </object>
          ) : isImage ? (
            // Image Viewer
            <img
              src={blobUrl}
              alt={attachment?.filename}
              className="max-w-full max-h-full object-contain rounded-sm"
              onError={() => setLoadError(true)}
            />
          ) : (
            // Unsupported type
            <div className="flex flex-col items-center gap-3 text-center">
              <Warning size={48} className="text-gray-500" />
              <p className="text-white font-medium">Preview not available</p>
              <p className="text-gray-400 text-sm">
                This file type cannot be previewed.
              </p>
              <button
                onClick={handleDownload}
                className="mt-2 px-4 py-2 bg-white/10 text-white rounded-sm hover:bg-white/20 transition-colors"
              >
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
